<template>
    <modal-pro></modal-pro>
    <template v-if="product">
        <section class="detail-hero" :style="{ backgroundImage: 'url(\'' + product.heroImg + '\')' }">
            <div class="hero-overlay"></div>
            <h1 class="sr-only" v-html="displayName"></h1>
            <div class="hero-tagline">
                <p>{{ heroText }}</p>
            </div>
        </section>

        <div class="highlights">
            <div class="highlight-item" v-for="(bullet, idx) in bullets" :key="idx" v-html="bullet"></div>
        </div>

        <div class="gallery">
            <img v-for="(img, idx) in galleryImages" :key="idx" :src="img" :alt="i18n" />
        </div>

        <div class="video-block" v-if="product.video">
            <iframe
                v-if="displayVideo"
                :src="videoEmbedUrl"
                class="video-iframe"
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowfullscreen
            ></iframe>
            <div class="video-thumb" v-else @click="displayVideo = true">
                <i class="fa-solid fa-circle-play play-icon"></i>
            </div>
        </div>

        <div class="cta-container">
            <router-link :to="{ name : 'Contact' }" class="btn">{{ $t('product.leafletBtn') }}</router-link>
        </div>

        <p class="legal-text" v-html="$t(i18n + '.legal', contentLocale)"></p>

        <product-range :product="product.range"></product-range>
    </template>
</template>

<script>
import ModalPro from "./../../components/ModalPro.vue";
import ProductRange from "./../../components/ProductsRange.vue";
import productDetails from "./../../data/productDetails.js";

export default {
    name : "ProductDetail",
    components : { ModalPro, ProductRange },
    data() {
        return {
            displayVideo : false
        }
    },
    computed : {
        i18n() {
            return this.$route.params.slug === "cedar-elm" ? "cage" : this.$route.params.slug;
        },
        contentLocale() {
            return this.$store.state.region === "us" ? "us" : this.$i18n.locale;
        },
        product() {
            return Object.values(productDetails).find(p => p.slug === this.$route.params.slug) || null;
        },
        displayName() {
            if (this.product && this.product.name) {
                return this.product.name;
            }
            return this.$te(this.i18n + ".title") ? this.$t(this.i18n + ".title") : "";
        },
        heroText() {
            if (this.i18n === "kypho") {
                return this.$t("kypho.title");
            }
            return this.$t(this.i18n + ".tagline", this.contentLocale);
        },
        galleryImages() {
            if (!this.product) return [];
            return this.product.gallery.map(img => {
                if (img && typeof img === "object" && img.fr) {
                    return this.$i18n.locale === "fr" ? img.fr : img.en;
                }
                return img;
            });
        },
        bullets() {
            const list = [];
            for (let i = 1; i <= 8; i++) {
                const key = this.i18n + ".bullet" + i;
                if (this.$te(key, this.contentLocale)) {
                    list.push(this.$t(key, this.contentLocale));
                }
            }
            return list;
        },
        videoEmbedUrl() {
            if (!this.product || !this.product.video) return "";
            const match = this.product.video.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/))([^#&?/]+)/);
            const videoId = match ? match[1] : "";
            return videoId ? `https://www.youtube.com/embed/${videoId}?autoplay=1` : "";
        }
    },
    watch : {
        '$route.params.slug' : function() {
            this.displayVideo = false;
            window.scrollTo(0, 0);
            this.checkRegion();
        },
        '$store.state.region' : function() {
            // avoid showing the content silently swap (EN ROW <-> EN US) under the visitor's eyes
            this.backToCategory();
        }
    },
    created() {
        this.checkRegion();
    },
    methods : {
        categoryRouteName() {
            if (!this.product) return "Products";
            const range = this.product.range;
            if (range === "CC") return "LC";
            if (range === "PS" || range === "VA" || range === "LC") return range;
            return "Products";
        },
        backToCategory() {
            this.$router.replace({ name : this.categoryRouteName() });
        },
        checkRegion() {
            if (this.product && this.$store.state.region === "us" && !this.product.usAvailable) {
                this.backToCategory();
            }
        }
    }
}
</script>

<style scoped lang="scss">
.detail-hero {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    width: 100%;
    min-height: 90vh;
    padding-top: 80px;
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
}

.hero-overlay {
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.15);
}

.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
}

.hero-tagline {
    position: relative;
    z-index: 1;
    max-width: 380px;
    margin-right: 8%;
    padding-left: 20px;
    border-left: 4px solid $mainColor;
}

.hero-tagline p {
    color: #fff;
    font-size: 22px;
    font-weight: 400;
    line-height: 1.4em;
    margin: 0;
}

.highlights {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-auto-rows: 340px;
    width: 100%;
    box-sizing: border-box;
    padding: 0 60px;
    background: $mainColor;
}

.highlight-item {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    text-align: center;
    overflow: hidden;
    padding: 25px 30px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 14px;
    font-weight: 300;
    line-height: 1.6em;
}

.highlight-item :deep(strong) {
    display: block;
    color: #fff;
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 8px;
}

.gallery {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    grid-template-rows: repeat(6, 1fr);
    aspect-ratio: 958 / 816;
    width: calc(100% - 120px);
    margin: 40px 60px 40px 60px;
    gap: 10px;
}

.gallery img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

.gallery img:nth-child(1) {
    grid-column: 1 / span 3;
    grid-row: 1 / span 4;
}

.gallery img:nth-child(2) {
    grid-column: 4 / span 3;
    grid-row: 1 / span 2;
}

.gallery img:nth-child(3) {
    grid-column: 4 / span 3;
    grid-row: 3 / span 2;
}

.gallery img:nth-child(4) {
    grid-column: 1 / span 4;
    grid-row: 5 / span 2;
}

.gallery img:nth-child(5) {
    grid-column: 5 / span 2;
    grid-row: 5 / span 2;
}

.video-block {
    width: $box;
    max-width: 90%;
    margin: 0 auto 40px auto;
    aspect-ratio: 16/9;
    background: #111;
}

.video-thumb {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
}

.video-iframe {
    width: 100%;
    height: 100%;
    display: block;
}

.play-icon {
    font-size: 60px;
    color: #fff;
    transition: 0.5s;
}

.video-thumb:hover .play-icon {
    color: $mainColor;
}

.cta-container {
    display: flex;
    justify-content: center;
    margin-bottom: 40px;
}

.legal-text {
    width: $box;
    max-width: 90%;
    margin: 0 auto 60px auto;
    color: #888;
    font-size: 12px;
    line-height: 1.6em;
    text-align: center;
}

@media screen and (max-width:768px) {
    .detail-hero {
        justify-content: center;
        min-height: 70vh;
    }

    .hero-tagline {
        margin-right: 0;
        max-width: 85%;
    }

    .hero-tagline p {
        font-size: 17px;
    }

    .highlights {
        grid-template-columns: 1fr;
        grid-auto-rows: auto;
    }

    .highlight-item {
        overflow: visible;
    }

    .gallery {
        display: flex;
        flex-direction: column;
        aspect-ratio: auto;
    }

    .gallery img {
        width: 100%;
        height: auto;
        aspect-ratio: 4 / 3;
    }
}
</style>
